


import { useState, useEffect, Component } from "react";
import { useSelector } from "react-redux";
import { EMPLOYER_URL } from "../../resources/resource";

// Error Boundary Component
class ErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 text-red-600">
          Error rendering component: {this.state.error?.message || "Unknown error"}
        </div>
      );
    }
    return this.props.children;
  }
}

const EmployeeDashboard = () => {
  const [activeMenu, setActiveMenu] = useState('profile');
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [employeeData, setEmployeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentUser = useSelector((state) => state.user.currentUser);

  // Fetch employee data
  useEffect(() => {
    const fetchEmployee = async () => {
      console.log('Fetching employee data for:', { userId: currentUser?._id, employerId: currentUser?.employer });
      if (!currentUser?._id || !currentUser?.employers) {
        console.error('Missing currentUser._id or currentUser.employer:', currentUser);
        setError('User data is incomplete. Please log in again.');
        setLoading(false);
        return;
      }
 
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}employer-employee/${currentUser._id}?employerId=${currentUser.employer}`, );
        console.log('API response status:', response.status);
        const data = await response.json();
        console.log('API response data:', data);
        if (response.ok) {
          setEmployeeData(data);
        } else {
          setError(data.error || 'Failed to fetch employee data');
        }
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message || 'Network error occurred');
      } finally {
        setLoading(false);
      }
    };
    fetchEmployee();
  }, [currentUser]);

  const toggleNav = () => {
    console.log('Toggling navbar, current state:', isNavOpen);
    setIsNavOpen((prev) => !prev);
  };

  const handleMenuClick = (menuName) => {
    console.log('Main menu clicked:', menuName);
    setActiveMenu(menuName);
    setActiveSubMenu(null);
    setIsNavOpen(false);
  };

  const handleSubMenuClick = (subMenuName) => {
    console.log('Sub-menu clicked:', subMenuName);
    setActiveSubMenu(subMenuName);
    setIsNavOpen(false);
  };

  // Handle leave request
  const handleLeaveRequest = async (e) => {
    e.preventDefault();
    const { startDate, endDate, reason } = e.target.elements;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}employer-employee/request-leave/${currentUser.employer}/${currentUser._id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            startDate: startDate.value,
            endDate: endDate.value,
            reason: reason.value,
          }),
        }
      );
      const data = await response.json();
      if (response.ok) {
        setEmployeeData({
          ...employeeData,
          leaveRequests: [...employeeData.leaveRequests, data.leaveRequest],
        });
        e.target.reset();
      } else {

        setError(data.error);
      }
    } catch (err) {
      console.log(err)
      setError(err.message);
    }
  };

  // Handle query response
  const handleQueryResponse = async (queryId, response) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}employer-employee/respond-query/${currentUser.employer}/${currentUser._id}/${queryId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ response }),
        }
      );
      const data = await res.json();
      if (res.ok) {
        setEmployeeData({
          ...employeeData,
          queries: employeeData.queries.map(q => q.queryId === queryId ? data.query : q),
        });
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Menu configuration for employee
  const menuConfig = [
    {
      name: 'profile',
      label: 'My Profile',
      icon: '👤',
      component: (
        <div className="p-6">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">My Profile</h2>
          <div className="bg-white p-6 rounded-lg shadow-lg">
            {employeeData && (
              <>
                <p><strong>Name:</strong> {employeeData.firstName} {employeeData.lastName}</p>
                <p><strong>Email:</strong> {employeeData.email}</p>
                <p><strong>Status:</strong> {employeeData.status}</p>
                {employeeData.status === 'Terminated' && (
                  <p><strong>Termination Reason:</strong> {employeeData.terminationReason}</p>
                )}
              </>
            )}
          </div>
        </div>
      ),
      subMenu: [
        { name: 'profile-sub1', label: 'Details', icon: '📋', component: (
          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Profile Details</h2>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <p className="text-gray-600">Full profile details.</p>
            </div>
          </div>
        )},
      ],
    },
    {
      name: 'leave',
      label: 'Leave Requests',
      icon: '🏖️',
      component: (
        <div className="p-6">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Leave Requests</h2>
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <form onSubmit={handleLeaveRequest}>
              <input
                type="date"
                name="startDate"
                className="w-full p-2 border rounded mb-2"
                required
              />
              <input
                type="date"
                name="endDate"
                className="w-full p-2 border rounded mb-2"
                required
              />
              <textarea
                name="reason"
                className="w-full p-2 border rounded mb-2"
                placeholder="Reason for leave"
                required
              />
              <button
                type="submit"
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Request Leave
              </button>
            </form>
            <div className="mt-6">
              {employeeData?.leaveRequests?.map((leave, index) => (
                <div key={index} className="mb-4 p-4 border rounded">
                  <p><strong>Start Date:</strong> {new Date(leave.startDate).toLocaleDateString()}</p>
                  <p><strong>End Date:</strong> {new Date(leave.endDate).toLocaleDateString()}</p>
                  <p><strong>Reason:</strong> {leave.reason}</p>
                  <p><strong>Status:</strong> {leave.status}</p>
                  <p><strong>Response:</strong> {leave.response || 'Pending'}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
      subMenu: [
        { name: 'leave-sub1', label: 'Leave History', icon: '🕒', component: (
          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Leave History</h2>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <p className="text-gray-600">View past leave requests.</p>
            </div>
          </div>
        )},
      ],
    },
    {
      name: 'queries',
      label: 'Queries',
      icon: '❓',
      component: (
        <div className="p-6">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Queries</h2>
          <div className="bg-white p-6 rounded-lg shadow-lg">
            {employeeData?.queries?.map(query => (
              <div key={query.queryId} className="mb-4 p-4 border rounded">
                <p><strong>Issued:</strong> {new Date(query.dateIssued).toLocaleDateString()}</p>
                <p><strong>Description:</strong> {query.description}</p>
                <p><strong>Response:</strong> {query.response || 'Pending'}</p>
                {!query.response && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const response = e.target.response.value;
                      handleQueryResponse(query.queryId, response);
                      e.target.reset();
                    }}
                  >
                    <textarea
                      name="response"
                      className="w-full p-2 border rounded"
                      placeholder="Enter response"
                    />
                    <button
                      type="submit"
                      className="mt-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                    >
                      Respond
                    </button>
                  </form>
                )}
              </div>
            ))}
          </div>
        </div>
      ),
      subMenu: [
        { name: 'queries-sub1', label: 'Query History', icon: '🕒', component: (
          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Query History</h2>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <p className="text-gray-600">View past queries.</p>
            </div>
          </div>
        )},
      ],
    },
    {
      name: 'attendance',
      label: 'Attendance',
      icon: '📅',
      component: (
        <div className="p-6">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Attendance</h2>
          <div className="bg-white p-6 rounded-lg shadow-lg">
            {employeeData?.attendance?.map((att, index) => (
              <div key={index} className="mb-4 p-4 border rounded">
                <p><strong>Date:</strong> {new Date(att.date).toLocaleDateString()}</p>
                <p><strong>Status:</strong> {att.status}</p>
                <p><strong>Notes:</strong> {att.notes || 'None'}</p>
              </div>
            ))}
          </div>
        </div>
      ),
      subMenu: [
        { name: 'attendance-sub1', label: 'Attendance Summary', icon: '📊', component: (
          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Attendance Summary</h2>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <p className="text-gray-600">View attendance trends.</p>
            </div>
          </div>
        )},
      ],
    },
    {
      name: 'remarks',
      label: 'Remarks',
      icon: '📝',
      component: (
        <div className="p-6">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Monthly Remarks</h2>
          <div className="bg-white p-6 rounded-lg shadow-lg">
            {employeeData?.remarks?.map((remark, index) => (
              <div key={index} className="mb-4 p-4 border rounded">
                <p><strong>Month/Year:</strong> {remark.month} {remark.year}</p>
                <p><strong>Comment:</strong> {remark.comment}</p>
              </div>
            ))}
          </div>
        </div>
      ),
      subMenu: [
        { name: 'remarks-sub1', label: 'Remarks History', icon: '🕒', component: (
          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Remarks History</h2>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <p className="text-gray-600">View past remarks.</p>
            </div>
          </div>
        )},
      ],
    },
  ];

  // Define currentMenu and currentSubMenu
  const currentMenu = menuConfig.find((item) => item.name === activeMenu) || menuConfig[0];
  const currentSubMenu = activeSubMenu ? currentMenu.subMenu.find((sub) => sub.name === activeSubMenu) : null;

  if (loading) return <div className="p-6">Loading...</div>;
  if (error) return <div className="p-6 text-red-600">Error: {error}</div>;

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-gray-800 text-white gradient-bg shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-xl font-bold">Employee Dashboard</h1>
            <div className="md:hidden">
              <button
                className="text-white text-2xl"
                onClick={toggleNav}
                aria-label={isNavOpen ? "Close menu" : "Open menu"}
              >
                {isNavOpen ? '✕' : '☰'}
              </button>
            </div>
            <div className="hidden md:flex md:items-center md:space-x-4">
              {menuConfig.map((item) => (
                <div key={item.name} className="relative group">
                  <button
                    className={`flex items-center px-3 py-2 text-sm font-medium hover:bg-blue-700 rounded-md transition ${
                      activeMenu === item.name ? 'bg-blue-700' : ''
                    }`}
                    onClick={() => handleMenuClick(item.name)}
                    aria-label={`Navigate to ${item.label}`}
                  >
                    <span className="mr-2">{item.icon}</span>
                    {item.label}
                  </button>
                  {activeMenu === item.name && (
                    <div className="absolute left-0 mt-2 w-48 bg-white text-gray-800 rounded-md shadow-lg hidden group-hover:block z-10">
                      {item.subMenu.map((sub) => (
                        <button
                          key={sub.name}
                          className={`w-full flex items-center px-4 py-2 text-sm hover:bg-gray-100 transition ${
                            activeSubMenu === sub.name ? 'bg-gray-100' : ''
                          }`}
                          onClick={() => handleSubMenuClick(sub.name)}
                          aria-label={`Navigate to ${sub.label}`}
                        >
                          <span className="mr-2">{sub.icon}</span>
                          {sub.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          {isNavOpen && (
            <div className="md:hidden">
              {menuConfig.map((item) => (
                <div key={item.name}>
                  <button
                    className={`w-full flex items-center px-4 py-2 text-sm font-medium hover:bg-blue-700 transition ${
                      activeMenu === item.name ? 'bg-blue-700' : ''
                    }`}
                    onClick={() => handleMenuClick(item.name)}
                    aria-label={`Navigate to ${item.label}`}
                  >
                    <span className="mr-2">{item.icon}</span>
                    {item.label}
                  </button>
                  {activeMenu === item.name && (
                    <div className="ml-4">
                      {item.subMenu.map((sub) => (
                        <button
                          key={sub.name}
                          className={`w-full flex items-center px-4 py-2 text-sm hover:bg-blue-600 transition ${
                            activeSubMenu === sub.name ? 'bg-blue-600' : ''
                          }`}
                          onClick={() => handleSubMenuClick(sub.name)}
                          aria-label={`Navigate to ${sub.label}`}
                        >
                          <span className="mr-2">{sub.icon}</span>
                          {sub.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <main className="flex-1 overflow-y-auto max-w-7xl mx-auto px-4 py-8">
          <ErrorBoundary>
            {currentSubMenu ? (
              currentSubMenu.component
            ) : currentMenu.component ? (
              currentMenu.component
            ) : (
              <div className="p-6 text-gray-600">Select a menu item to view content.</div>
            )}
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
};

export default EmployeeDashboard;